/**
 * Agent (the Compliance Agent, operator order 2026-10-23). The compliance
 * team's page: today's organization brief, the questions queue, and Setup.
 * The server refuses anyone else. House layout: white cards, slate, one
 * orange.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { api } from './api.js';
import SetupTab from './SetupTab.jsx';
import BriefTab from './BriefTab.jsx';
import QuestionsTab from './QuestionsTab.jsx';

const TABS = [['brief', 'Brief'], ['questions', 'Questions'], ['setup', 'Setup']];

export default function AgentApp({ initialData = null, initialTab = 'brief' }) {
  const [tab, setTab] = useState(initialTab);
  const [data, setData] = useState(initialData); // initialData: the render harness only ({ setup, brief, people, questions })
  const [err, setErr] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = () => setRefreshKey((k) => k + 1);

  const load = useCallback(async () => {
    const [setup, brief, qs] = await Promise.all([api('/setup'), api('/brief'), api('/questions')]);
    if (!setup?.success) { setErr(setup?.message || 'Could not load'); return; }
    setData({ setup, brief: brief?.brief || null, people: brief?.people || {}, questions: qs?.questions || [] });
  }, []);
  useEffect(() => { if (!initialData) load(); }, [load, refreshKey, initialData]);

  if (err) return <div className="p-10 text-sm text-rose-600">The agent page could not load: {err}</div>;
  if (!data) return <div className="p-10 text-sm text-slate-400">Loading the agent…</div>;
  const openQ = (data.questions || []).filter((q) => q.status === 'open').length;

  return (
    <div className="h-full overflow-auto bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Agent</h1>
            <p className="text-sm text-slate-500 mt-0.5">The Compliance Agent watches the apps for what is due, late or stalled, tells each person only what concerns them, and brings the organization to the compliance team here, questions first.</p>
          </div>
          <nav className="inline-flex rounded-full bg-white border border-slate-200 p-0.5 text-sm">
            {TABS.map(([k, label]) => (
              <button key={k} type="button" onClick={() => setTab(k)} className={`px-4 py-1.5 rounded-full transition ${tab === k ? 'bg-slate-900 text-white' : 'text-slate-600 hover:text-slate-900'}`}>
                {label}{k === 'questions' && openQ > 0 ? <span className="ml-1.5 text-[11px] text-[#E5601F]">{openQ}</span> : null}
              </button>
            ))}
          </nav>
        </div>
        <div className="mt-6">
          {tab === 'brief' && <BriefTab brief={data.brief} people={data.people} onAnswer={() => setTab('questions')} />}
          {tab === 'questions' && <QuestionsTab questions={data.questions || []} onRefresh={refresh} />}
          {tab === 'setup' && <SetupTab data={data.setup} onRefresh={refresh} />}
        </div>
      </div>
    </div>
  );
}
